import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { PlaidAccountError } from 'api/PlaidApi';
import { setPlaidAccountError } from 'handlers/bankAccount';
import { getBankAccount } from 'selectors/getBankAccount';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import { validateIdentity } from 'thunks';

interface UsePlaidValidateIdentityOptions {
  applicationId: string | undefined;
}

interface UsePlaidValidateIdentityReturn {
  runValidateIdentity: (params?: { userConfirmedAccountOwnership?: boolean }) => Promise<boolean>;
  clearPlaidAccountError: () => void;
  plaidAccountError: PlaidAccountError | null;
  isLoadingSendPlaidSuccess: boolean;
}

const usePlaidValidateIdentity = ({
  applicationId,
}: UsePlaidValidateIdentityOptions): UsePlaidValidateIdentityReturn => {
  const dispatch = useDispatch();
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const { plaidAccountError, isLoadingSendPlaidSuccess } = useSelector(getBankAccount);

  const clearPlaidAccountError = useCallback(() => {
    dispatch(setPlaidAccountError(null));
  }, [dispatch]);

  const runValidateIdentity = useCallback(
    async (params?: { userConfirmedAccountOwnership?: boolean }): Promise<boolean> => {
      try {
        await dispatchWithUnwrap(validateIdentity({ applicationId: applicationId!, ...params }));
        return true;
      } catch {
        return false;
      }
    },
    [dispatchWithUnwrap, applicationId],
  );

  return { runValidateIdentity, clearPlaidAccountError, plaidAccountError, isLoadingSendPlaidSuccess };
};

export default usePlaidValidateIdentity;
